import Logger from '../Logger';
import * as requestActions from '../store/actions/requestActions';
import * as settingsActions from '../store/actions/settingsActions';

const logger = new Logger('AudioDeviceManager');

/**
 * Helper function to switch to the next available audio input device
 * when the current device is disconnected
 * @param {Object} roomClient - The RoomClient instance
 * @param {boolean} wasMicProducerPaused - Whether the mic was muted before disconnection
 * @param {string} source - Source of the switch (for logging) - 'trackended' or 'devicechange'
 * @param {Object} store - The Redux store
 * @param {Object} intl - react-intl object
 * @returns {Promise<boolean>} - Returns true if switch was successful, false otherwise
 */
export async function switchToNextAudioDevice(roomClient, wasMicProducerPaused, source, store, intl)
{
	logger.debug('switchToNextAudioDevice() [source:"%s"]', source);

	try
	{
		// Update available devices
		await roomClient._updateAudioDevices();

		// Check if there are available devices to switch to
		const availableDevices = Object.values(roomClient._audioDevices);

		if (availableDevices.length === 0)
		{
			logger.warn('switchToNextAudioDevice() | no available audio devices found');
			return false;
		}

		const nextDeviceId = availableDevices[0].deviceId;

		logger.debug('switchToNextAudioDevice() | switching to device [deviceId:"%s", label:"%s"]', 
			nextDeviceId, availableDevices[0].label || 'Unknown');

		// Set the new device as selected
		store.dispatch(settingsActions.setSelectedAudioDevice(nextDeviceId));

		// Start the mic producer with the new device
		await roomClient.updateMic({ start: true, newDeviceId: nextDeviceId });

		// Restore the previous mute state
		if (wasMicProducerPaused)
		{
			await roomClient.muteMic();
		}

		logger.debug('switchToNextAudioDevice() | successfully switched to new audio device');

		// Show a notification that device was switched
		if (intl && intl.formatMessage)
		{
			store.dispatch(requestActions.notify(
				{
					type : 'info',
					text : intl.formatMessage({
						id             : 'devices.microphoneSwitched',
						defaultMessage : 'Microphone disconnected, switched to another device'
					})
				}));
		}
		else
		{
			// Fallback notification if intl is not available yet
			store.dispatch(requestActions.notify(
				{
					type : 'info',
					text : 'Microphone disconnected, switched to another device'
				}));
		}

		return true;
	}
	catch (error)
	{
		logger.error('switchToNextAudioDevice() | error switching audio device [error:"%o"]', error);
		return false;
	}
}

/**
 * Creates and returns a device change listener function
 * Note: Device switching is handled by the trackended event handler, not here.
 * This listener only updates device lists and shows notifications.
 * @param {Object} roomClient - The RoomClient instance
 * @param {Object} store - The Redux store
 * @param {Function} getIntl - Function that returns the current intl object (can be undefined initially)
 * @returns {Function} - The device change event listener function
 */
function createDeviceChangeListener(roomClient, store, getIntl)
{
	return async () =>
	{
		logger.debug('createDeviceChangeListener() | navigator.mediaDevices.ondevicechange');

		// Update device lists
		await roomClient._updateAudioDevices();
		await roomClient._updateWebcams();
		await roomClient._updateAudioOutputDevices();

		// Get current intl value (it might be initialized later)
		const intl = typeof getIntl === 'function' ? getIntl() : getIntl;

		// Show notification that devices changed
		// Note: Actual device switching is handled by trackended event handler
		// to avoid duplicate switching when both events fire simultaneously
		if (intl && intl.formatMessage)
		{
			store.dispatch(requestActions.notify(
				{
					text : intl.formatMessage({
						id             : 'devices.devicesChanged',
						defaultMessage : 'Your devices changed, configure your devices in the settings dialog'
					})
				}));
		}
		else
		{
			// Fallback notification if intl is not available yet
			store.dispatch(requestActions.notify(
				{
					text : 'Your devices changed, configure your devices in the settings dialog'
				}));
		}
	};
}

/**
 * Starts listening for device changes and sets up the event listener
 * @param {Object} roomClient - The RoomClient instance
 * @param {Object} store - The Redux store
 * @param {Object|Function} intlOrGetter - react-intl object or a function that returns it (can be undefined initially)
 */
export function startDeviceChangeListener(roomClient, store, intlOrGetter)
{
	logger.debug('startDeviceChangeListener() | setting up device change listener');
	
	const deviceChangeHandler = createDeviceChangeListener(roomClient, store, intlOrGetter);
	
	navigator.mediaDevices.addEventListener('devicechange', deviceChangeHandler);
}
